// What kind of browser the page is running in. The /start flow changes a few
// things for in-app browsers (Instagram, Facebook, TikTok…): Google sign-in is
// blocked there, iOS cannot add a .pkpass to Wallet, and downloads are
// unreliable. Kept free of React so analytics and Bugsnag can use it too.

const currentUserAgent = () =>
  typeof navigator !== "undefined" ? navigator.userAgent || "" : "";

export function getPlatform(userAgent = currentUserAgent()) {
  const ua = userAgent || "";
  if (/\b(iPhone|iPad|iPod)\b/.test(ua)) return "ios";
  // iPadOS Safari asks for desktop sites with a Mac user agent; a touch screen
  // gives it away.
  if (
    /\bMacintosh\b/.test(ua) &&
    typeof navigator !== "undefined" &&
    navigator.maxTouchPoints > 1
  ) {
    return "ios";
  }
  if (/\bAndroid\b/.test(ua)) return "android";
  return "desktop";
}

export function isMobile(userAgent = currentUserAgent()) {
  return getPlatform(userAgent) !== "desktop";
}

// Named in-app browsers first, then generic app web views. iOS app web views
// leave the Safari/ token out of the user agent (Safari, SFSafariViewController
// and Chrome iOS keep it); Android ones carry "; wv)".
const IN_APP_BROWSERS = [
  ["instagram", /\bInstagram\b/i],
  ["messenger", /\bMessenger(ForiOS)?\b|FBAN\/Messenger/i],
  ["facebook", /\bFBAN\b|\bFBAV\b|\bFB_IAB\b|\bFBIOS\b|\bFB4A\b/i],
  // TikTok writes "musical_ly_41.0.0", so no word boundary after the name.
  ["tiktok", /musical_ly|Bytedance|\bTikTok\b/i],
  ["snapchat", /\bSnapchat\b/i],
  ["linkedin", /\bLinkedInApp\b/i],
  ["line", /\bLine\//],
];

export function getInAppBrowser(userAgent = currentUserAgent()) {
  const ua = userAgent || "";
  for (const [name, pattern] of IN_APP_BROWSERS) {
    if (pattern.test(ua)) return name;
  }
  const platform = getPlatform(ua);
  if (platform === "ios" && /\bAppleWebKit\b/.test(ua) && !/\bSafari\//.test(ua)) {
    return "webview";
  }
  if (platform === "android" && /;\s*wv\)/.test(ua)) return "webview";
  return null;
}

export function isInAppBrowser(userAgent = currentUserAgent()) {
  return getInAppBrowser(userAgent) !== null;
}

export function getBrowserContext(userAgent = currentUserAgent()) {
  const inAppBrowser = getInAppBrowser(userAgent);
  return {
    platform: getPlatform(userAgent),
    inAppBrowser,
    isInAppBrowser: inAppBrowser !== null,
  };
}

// An Android intent link that opens an https URL in Chrome. Instagram and
// Facebook on Android hand intent:// links to the system, which leaves the
// in-app browser; when Chrome is missing Android opens the fallback instead.
// Must be used as a plain link the person taps, never an automatic redirect.
export function chromeIntentUrl(httpsUrl, fallbackUrl) {
  const url = new URL(httpsUrl);
  const fallback = fallbackUrl || httpsUrl;
  return (
    `intent://${url.host}${url.pathname}${url.search}` +
    `#Intent;scheme=https;package=com.android.chrome;` +
    `S.browser_fallback_url=${encodeURIComponent(fallback)};end`
  );
}
